import { useState, useEffect, useCallback } from 'react';

interface UseGlobalSearchReturn {
    isSearchVisible: boolean;
    searchQuery: string;
    showSearch: (query?: string) => void;
    hideSearch: () => void;
}

export const useGlobalSearch = (): UseGlobalSearchReturn => {
    const [isSearchVisible, setIsSearchVisible] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Función para verificar si un elemento es un input/textarea/contenteditable
    const isInputElement = useCallback((element: Element | null): boolean => {
        if (!element) return false;
        
        const tagName = element.tagName.toLowerCase();
        const inputTypes = ['input', 'textarea', 'select'];
        
        // Verificar si es un elemento de input
        if (inputTypes.includes(tagName)) return true;
        
        // Verificar si es contenteditable
        if (element.getAttribute('contenteditable') === 'true') return true;
        
        // Verificar si está dentro de un elemento contenteditable
        let parent = element.parentElement;
        while (parent) {
            if (parent.getAttribute('contenteditable') === 'true') return true;
            parent = parent.parentElement;
        }
        
        return false;
    }, []);

    // Manejar eventos de teclado globales - SOLO PARA LA BARRA ESPACIADORA
    const handleGlobalKeyDown = useCallback((event: KeyboardEvent) => {
        // Verificar si la tecla presionada es la barra espaciadora
        if (event.key === ' ') {
            // Ignorar si hay modificadores presionados (Ctrl, Alt, etc.)
            if (event.ctrlKey || event.altKey || event.metaKey) return;
            
            // Ignorar si ya está visible el buscador
            if (isSearchVisible) return;
            
            // Ignorar si el foco está en un input
            if (isInputElement(document.activeElement)) return;
            
            // Prevenir comportamiento por defecto (scroll hacia abajo)
            event.preventDefault();
            
            // Mostrar buscador con query vacío cuando se presiona espacio
            setSearchQuery('');
            setIsSearchVisible(true);
        }
        // Si no es la barra espaciadora, no hacemos nada (eliminando la lógica anterior)
    }, [isSearchVisible, isInputElement]);

    // Función para mostrar el buscador manualmente (por ejemplo, al hacer clic en el botón)
    const showSearch = useCallback((query: string = '') => {
        setSearchQuery(query);
        setIsSearchVisible(true);
    }, []);

    // Función para ocultar el buscador
    const hideSearch = useCallback(() => {
        setIsSearchVisible(false);
        setSearchQuery('');
    }, []);

    // Agregar/remover event listeners
    useEffect(() => {
        document.addEventListener('keydown', handleGlobalKeyDown);
        
        return () => {
            document.removeEventListener('keydown', handleGlobalKeyDown);
        };
    }, [handleGlobalKeyDown]);

    // Manejar tecla Escape globalmente para cerrar el buscador
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isSearchVisible) {
                hideSearch();
            }
        };

        if (isSearchVisible) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [isSearchVisible, hideSearch]);

    return {
        isSearchVisible,
        searchQuery,
        showSearch,
        hideSearch
    };
};